import { MobModel } from "types";

import aethyrPilotImage from "assets/mob-aethyr-pilot.svg";
import aethyrShipImage from "assets/mob-aethyr-ship.svg";
import calibraPilotImage from "assets/mob-calibra-pilot.svg";
import calibraShipImage from "assets/mob-calibra-ship.svg";
import flairePilotImage from "assets/mob-flaire-pilot.svg";
import flaireShipImage from "assets/mob-flaire-ship.svg";
import luxonPilotImage from "assets/mob-luxon-pilot.svg";
import luxonShipImage from "assets/mob-luxon-ship.svg";
import phantesPilotImage from "assets/mob-phantes-pilot.svg";
import phantesShipImage from "assets/mob-phantes-ship.svg";

import { BASE_STATS } from "data/baseStats";

const { FIREPOWER, RESILIENCE, SPEED, PRECISION, ENERGY } = BASE_STATS;

// SECTOR TIER 4 - MEDALLION RANGE: 181-300

const SENECA_MERIDIAN_MOBS: Record<string, MobModel> = {
  luxon: {
    name: "Luxon",
    images: {
      ship: luxonShipImage,
      pilot: luxonPilotImage,
    },
    dialogs: {
      opening:
        "Welcome to inside the Core. Not every ship can handle the intensity of antimatter in here.",
      win: "I guess you can handle being in the Core. Welcome.",
      lose: "You're not ready to be in the Core.",
    },
    position: 40,
    heightPercentage: 45,
    medallions: 190,
    baseStatsModifiers: {
      firepower: 2,
      resilience: 4,
      speed: 2,
      precision: 3,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 3,
      resilience: 2,
      speed: 2,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [FIREPOWER, RESILIENCE, PRECISION, ENERGY, SPEED],
    isBoss: false,
    drops: {
      platinum: "bastion_energa",
      gold: "ward_energa",
      silver: "nova_ward",
      bronze: "cosmic_sight",
    },
    installedUpgrades: {
      weapons: null,
      shields: "nova_ward",
      thrusters: null,
      targetingSystem: "cosmic_sight",
      reactorCore: "bastion_energa",
    },
    partPositions: {
      weapons: { x: 60, y: 66.5 },
      targeting: { x: 70, y: 66.5 },
      thrusters: { x: 11, y: 57 },
    },
    skills: ["shieldStorm", "energyBlast", "emergencyRepairs", "shieldRestore"],
  },
  phantes: {
    name: "Phantes",
    images: {
      ship: phantesShipImage,
      pilot: phantesPilotImage,
    },
    dialogs: {
      opening: "This will be quick work. Or 'quork' to say it even quicker.",
      win: "This really was not quork.",
      lose: "Told you.",
    },
    position: 80,
    heightPercentage: 45,
    medallions: 210,
    baseStatsModifiers: {
      firepower: 2,
      resilience: 3,
      speed: 4,
      precision: 2,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 3,
      resilience: 2,
      speed: 2,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [SPEED, RESILIENCE, ENERGY, FIREPOWER, PRECISION],
    isBoss: false,
    drops: {
      platinum: "flash_energa",
      gold: "dash_energa",
      silver: "quantum_drive",
      bronze: "nova_fortress",
    },
    installedUpgrades: {
      weapons: null,
      shields: "nova_fortress",
      thrusters: "quantum_drive",
      targetingSystem: null,
      reactorCore: "flash_energa",
    },
    partPositions: {
      weapons: { x: 59, y: 58 },
      targeting: { x: 69, y: 58 },
      thrusters: { x: 30, y: 52.25 },
    },
    skills: ["phantomStrike", "rapidFire", "energyBlast", "hyperShift"],
  },
  flaire: {
    name: "Flaire",
    images: {
      ship: flaireShipImage,
      pilot: flairePilotImage,
    },
    dialogs: {
      opening: "My weapons love being in the Core. They told me.",
      win: "My weapons told me they did not enjoy losing.",
      lose: "My weapons told me you kind of suck.",
    },
    position: 120,
    heightPercentage: 45,
    medallions: 235,
    baseStatsModifiers: {
      firepower: 4,
      resilience: 2,
      speed: 3,
      precision: 2,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 2,
      resilience: 2,
      speed: 2,
      precision: 3,
      energy: 1,
    },
    baseStatsTargets: [SPEED, ENERGY, RESILIENCE, PRECISION, FIREPOWER],
    isBoss: false,
    drops: {
      platinum: "inferno_energa",
      gold: "tempest_energa",
      silver: "void_lancer",
      bronze: "atomic_drive",
    },
    installedUpgrades: {
      weapons: "void_lancer",
      shields: null,
      thrusters: "atomic_drive",
      targetingSystem: null,
      reactorCore: "inferno_energa",
    },
    partPositions: {
      weapons: { x: 62, y: 63.25 },
      targeting: { x: 72, y: 63.25 },
      thrusters: { x: 14.5, y: 56.75 },
    },
    skills: ["energyBlast", "reactorOverload", "powerSurge", "shieldBreaker"],
  },
  calibra: {
    name: "Calibra",
    images: {
      ship: calibraShipImage,
      pilot: calibraPilotImage,
    },
    dialogs: {
      opening:
        "Being inside the Core makes me more accurate. ...How? Antimatter... waves?",
      win: "Oh no, the antimatter waves failed me!!",
      lose: "Thank you antimatter waves, I love you.",
    },
    position: 160,
    heightPercentage: 45,
    medallions: 265,
    baseStatsModifiers: {
      firepower: 3,
      resilience: 2,
      speed: 2,
      precision: 4,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 2,
      resilience: 2,
      speed: 3,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [PRECISION, FIREPOWER, ENERGY, RESILIENCE, SPEED],
    isBoss: false,
    drops: {
      platinum: "lock_energa",
      gold: "razor_energa",
      silver: "astral_sight",
      bronze: "void_beam",
    },
    installedUpgrades: {
      weapons: "void_beam",
      shields: null,
      thrusters: null,
      targetingSystem: "astral_sight",
      reactorCore: "lock_energa",
    },
    partPositions: {
      weapons: { x: 66, y: 58.5 },
      targeting: { x: 76, y: 58.5 },
      thrusters: { x: 18, y: 51.5 },
    },
    skills: ["pressurePoint", "eagleEye", "energyBlast", "systemsDecay"],
  },
  aethyr: {
    name: "Aethyr",
    images: {
      ship: aethyrShipImage,
      pilot: aethyrPilotImage,
    },
    dialogs: {
      opening: "Sigh, so many pilots want to fight me. It bores me.",
      win: "You... don't bore me? I think... I love you? Uh, never mind.",
      lose: "You bore me too.",
    },
    position: 200,
    heightPercentage: 45,
    medallions: 300,
    baseStatsModifiers: {
      firepower: 1,
      resilience: 4,
      speed: 3,
      precision: 2,
      energy: 5,
    },
    baseStatsCosts: {
      firepower: 3,
      resilience: 2,
      speed: 2,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [ENERGY, FIREPOWER, RESILIENCE, PRECISION, SPEED],
    isBoss: false,
    drops: {
      platinum: "matrix_ultima",
      gold: "matrix_infinite",
      silver: "mega_core",
      bronze: "surge_core",
    },
    installedUpgrades: {
      weapons: null,
      shields: "nova_ward",
      thrusters: "quantum_drive",
      targetingSystem: "cosmic_sight",
      reactorCore: "matrix_ultima",
    },
    partPositions: {
      weapons: { x: 59.5, y: 64.25 },
      targeting: { x: 69.5, y: 64.25 },
      thrusters: { x: 24.25, y: 58 },
    },
    skills: [
      "shieldBypass",
      "energyBlast",
      "reactorOverload",
      "emergencyRepairs",
      "pressurePoint",
    ],
  },
};

export default SENECA_MERIDIAN_MOBS;
